import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildCampaignPerformanceFetchKey,
  buildClientEventsPerformanceQuery,
  formatCampaignPerformanceDate,
  normalizeCampaignPerformancePayload,
} from "../../lib/utils/campaignPerformanceReport"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

function campaignArgKey(arg = {}) {
  const { clientId, startDate, endDate } = arg
  return buildCampaignPerformanceFetchKey(
    clientId,
    formatCampaignPerformanceDate(startDate),
    formatCampaignPerformanceDate(endDate),
  )
}

/**
 * GET /api/v1/report/post/clients/{clientId}/events/performance?startDate=&endDate=
 */
export const fetchClientCampaignPerformance = createAsyncThunk(
  "clientCampaignPerformance/fetch",
  async ({ clientId, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }

      const start = formatCampaignPerformanceDate(startDate)
      const end = formatCampaignPerformanceDate(endDate)
      if (!start || !end) {
        return rejectWithValue("startDate and endDate are required")
      }

      const query = buildClientEventsPerformanceQuery({ startDate: start, endDate: end })
      const data = await get(`report/post/clients/${clientId}/events/performance?${query}`)

      return {
        events: normalizeCampaignPerformancePayload(data),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildCampaignPerformanceFetchKey(clientId, start, end),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch event performance")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      return !skipIfFetchKeyLoaded(getState().clientCampaignPerformance, campaignArgKey(arg))
    },
  },
)

const initialState = {
  events: [],
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginFetch(state, action) {
  const nextKey = campaignArgKey(action.meta.arg || {})
  const keepStaleEvents =
    nextKey === state.fetchedFetchKey && Array.isArray(state.events) && state.events.length > 0

  state.status = "loading"
  state.error = null
  state.requestedFetchKey = nextKey
  if (!keepStaleEvents) {
    state.fetchedFetchKey = null
    state.events = []
  }
}

function isStaleFetch(action, state) {
  return campaignArgKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")
}

const clientCampaignPerformanceSlice = createSlice({
  name: "clientCampaignPerformance",
  initialState,
  reducers: {
    clearClientCampaignPerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientCampaignPerformance.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientCampaignPerformance.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.events = action.payload?.events ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientCampaignPerformance.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch event performance"
        state.events = []
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientCampaignPerformance } = clientCampaignPerformanceSlice.actions
export default clientCampaignPerformanceSlice.reducer
